import { ChevronDown, Plus } from 'lucide-react'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { ViewsResponse } from '../query/types'
import { tabViews, viewLabel, viewSearch } from './tabViews'
import { ViewMenu } from './ViewMenu'

/**
 * The views of a list as tabs (design §5.4): the standard views, then the pinned ones, then the
 * open one when it is not pinned. A tab is a link, so it can be opened in a new tab; it keeps the
 * search term and drops the rest (`viewSearch`). "Weergave" makes a new view of the current state,
 * "Alle weergaven" opens the list with all of them. From 768 px up; a phone has `ViewSelect`.
 */
export function ViewTabs({
  data,
  currentKey,
  params,
  onNewView,
  onAllViews,
  onSaveAsList,
}: {
  data: ViewsResponse
  /** The `key` of the open view. */
  currentKey: string
  /** The parameters of the list now. */
  params: URLSearchParams
  /** Without the two view buttons the tabs are fixed (Lijsten has no saved views). */
  onNewView?: () => void
  onAllViews?: () => void
  /** Adds the "⋯" with "Opslaan als lijst…" (Contacten). */
  onSaveAsList?: () => void
}) {
  return (
    <nav
      aria-label="Weergaven"
      className="flex items-center gap-2 rounded-xl border border-border bg-white p-1.5 shadow-sm max-md:hidden"
    >
      {/* The views scroll sideways on a narrow screen instead of wrapping. */}
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:thin]">
        {tabViews(data, currentKey).map((view) => {
          const current = view.key === currentKey
          return (
            <Link
              key={view.key}
              to={{ search: viewSearch(params, view.key).toString() }}
              aria-current={current ? 'page' : undefined}
              title={view.description ?? undefined}
              className={cn(
                'inline-flex h-10 shrink-0 items-center whitespace-nowrap rounded-lg px-4 text-sm transition-colors',
                current
                  ? 'bg-primary font-semibold text-primary-foreground shadow-sm'
                  : 'font-medium text-foreground/80 hover:bg-muted hover:text-foreground',
              )}
            >
              {viewLabel(view)}
            </Link>
          )
        })}
      </div>
      {onNewView || onAllViews || onSaveAsList ? (
        <div className="flex shrink-0 items-center gap-1 border-l border-border pl-2">
          {onNewView ? (
            <Button type="button" variant="ghost" size="sm" className="h-10 text-muted-foreground hover:text-foreground" aria-label="Nieuwe weergave" onClick={onNewView}>
              <Plus aria-hidden /> Weergave
            </Button>
          ) : null}
          {onAllViews ? (
            <Button type="button" variant="outline" size="sm" className="h-10" onClick={onAllViews}>
              Alle weergaven <ChevronDown aria-hidden />
            </Button>
          ) : null}
          {onSaveAsList ? <ViewMenu onSaveAsList={onSaveAsList} className="h-10" /> : null}
        </div>
      ) : null}
    </nav>
  )
}
